import { TitanButton } from "@lib/titan2/dist/game/control/TitanButton";
import { TitanPanel } from "@lib/titan2/dist/game/control/TitanPanel";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { EventHandler } from "@lib/titan2/dist/util/event/EventHandler";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { SlingoEvent } from "../../../controller/event/SlingoEvent";
import { SlingoLang } from "../lang/SlingoLang";
import { AbstractGameView } from "./AbstractGameView";
/**
 * AbstractPayTable.ts
 */
export class AbstractPayTable extends AbstractGameView {
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    postCreate() {
        super.postCreate();
        Logger.addObjectRef(this, "PayTable");
    }
    create() {
        super.create();
        this.createCellPanel();
        this.createCells();
        this.createBackToGameButton();
    }
    createCellPanel() {
        if (this.landscapeProperties.panelProperties) {
            this.cellPanel = TitanPanel.createPanel(this, "cellPanel", this.landscapeProperties.panelProperties, this.portraitProperties?.panelProperties);
        }
    }
    createCells() {
        this.cells = [];
        for (let i = 0; i < 11; i++) {
            this.createCell(i);
        }
        this.cellPanel.arrange();
    }
    createCell(childIndex) {
        const cell = this.factory.createPayTableCell(this.uiProperties.cellProperties[childIndex]);
        const prefix = this.uiProperties.cellProperties[childIndex].useShortLabel ? SlingoLang.SLINGO_SHORT : SlingoLang.SLINGO;
        const id = this.uiProperties.cellProperties[childIndex].id;
        cell.setLabel(prefix + (id === "11" ? "12" : id));
        this.cellPanel.addToPanel(cell, false);
        this.cells.push(cell);
    }
    createBackToGameButton() {
        if (this.landscapeProperties.backToGameButtonProperties) {
            this.backToGameButton = TitanButton.createButton(this, this.landscapeProperties.backToGameButtonProperties, this.portraitProperties?.backToGameButtonProperties, this.handleBackToGameSelected.bind(this));
            this.backToGameButton.name = "backToGameButton";
        }
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.handlePurchaseEntry);
        this.registerGame(CommonGameEvent.STAKE_UPDATED, this.handleStakeUpdated);
        this.registerGame(SlingoEvent.SLINGO_PAY_TABLE, this.handleSlingoPayTable);
        this.registerGame(SlingoEvent.AVAILABLE_WIN, this.handleAvailableWin);
        this.registerGame(SlingoEvent.AVAILABLE_WIN_CLEAR, this.handleAvailableWinClear);
        this.registerGame(CommonGameEvent.SPIN_REQUEST, this.handleSpinRequest);
        this.registerGame(CommonGameEvent.COLLECT_REQUEST, this.handleCollectRequest);
        this.registerGame(CommonGameEvent.STANDARD_SPIN_RECOVERY, this.handleStandardSpinRecovery);
        this.registerGame(CommonGameEvent.FREE_SPIN_RECOVERY, this.handleFreeSpinRecovery);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN_RECOVERY, this.handlePurchaseSpinRecovery);
    }
    handleBackToGameSelected() {
        this.callHandler(this.dismissHandler);
    }
    onDismiss(action, scope) {
        this.dismissHandler = new EventHandler(action, scope);
    }
    setPrimary(value) {
        this.uiProperties.landscapePrimary = value;
        this.uiProperties.portraitPrimary = value;
    }
    handleStandardSpinRecovery() {
        this.handlePurchaseEntry();
    }
    handleFreeSpinRecovery() {
        this.handlePurchaseEntry();
    }
    handlePurchaseSpinRecovery() {
        this.handlePurchaseEntry();
    }
    handlePurchaseEntry() {
        this.clearHighlight();
        this.updateActivePrize();
        this.updatePrizeValues();
    }
    handleStakeUpdated() {
        this.updatePrizeValues();
    }
    handleSlingoPayTable() {
        let slingosAwarded = this.model.getSlingosAwarded();
        let matchedPatterns = this.model.getCurrentMatchedPatterns();
        let delay = 0;
        for (let index = slingosAwarded - 1; index >= 0; index--) {
            this.callAfter(delay, this.handleMatchedPatterns, matchedPatterns - index);
            delay += this.uiProperties.matchedPatternInterval;
        }
        this.callAfter(delay, this.handleMatchedPatternsComplete);
    }
    handleMatchedPatterns(numberOfPatterns) {
        if (numberOfPatterns > 0) {
            this.dispatchGame(SlingoEvent.getPatternEvent(numberOfPatterns));
            this.updateCells(numberOfPatterns);
        }
    }
    handleMatchedPatternsComplete() {
        if (!this.isPrimary()) {
            return;
        }
        Logger.debug("PayTable: handleMatchedPatternsComplete()");
        if (this.model.getCurrentMatchedPatterns() == this.model.getGameProgress().getMatchedPatterns()) {
            //all dabs complete, ready for next spin
            this.dispatchGame(SlingoEvent.SLINGO_COMPLETE);
        }
        else {
            //check for finished slingo, but still dabs to do
            const dabData = this.model.getDabData();
            if (dabData.getAutoDabIndex() < dabData.getAutoDabCells().length) {
                this.dispatchGame(SlingoEvent.SLINGO_COMPLETE);
            }
        }
    }
    handleAvailableWinClear() {
        this.clearHighlight();
    }
    handleSpinRequest() {
        if (this.model.isPurchaseSpin()) {
            this.clearHighlight();
        }
    }
    handleCollectRequest() {
        this.clearHighlight();
    }
    clearHighlight() {
        if (this.highlightedCell) {
            this.highlightedCell.inactive();
            this.highlightedCell = null;
        }
    }
    handleAvailableWin() {
        let availableWin = this.model.getAvailableWin();
        let numberOfPatterns = availableWin.getNumberOfPatterns();
        let cellIndex = this.cells.findIndex(cell => {
            let idNum = parseInt(cell.getId(), 10);
            if (idNum === 11) {
                idNum = 12;
            }
            return idNum === numberOfPatterns;
        });
        this.highlightedCell = this.cells[cellIndex];
        this.highlightedCell.highlight();
    }
    updatePrizeValues() {
        const payoutConfig = this.model.getPayoutConfig();
        const currentStake = this.model.getCurrentStake();
        const payouts = payoutConfig.getPatternPayouts();
        payouts.reverse();
        payouts.splice(1, 1);
        let value;
        for (let i = 0; i < this.cells.length; i++) {
            value = currentStake * payouts[i];
            this.cells[i].update(value);
        }
        this.dispatchGame(SlingoEvent.PAY_TABLE_UPDATE);
    }
    updateActivePrize() {
        let matchedPatterns = this.model.getCurrentMatchedPatterns();
        this.updateCells(matchedPatterns);
    }
    updateCells(numberOfPatterns) {
        let sortedCells = this.cells.slice().reverse();
        let lastCellIndex = sortedCells.length - 1;
        let cell;
        let currentPrize;
        let activePrize;
        for (let i = 0; i < lastCellIndex; i++) {
            cell = sortedCells[i];
            currentPrize = (numberOfPatterns) - 1 == i;
            activePrize = i < numberOfPatterns;
            this.updateCell(cell, currentPrize, activePrize);
        }
        currentPrize = numberOfPatterns - 1 >= lastCellIndex;
        cell = sortedCells[lastCellIndex];
        this.updateCell(cell, currentPrize, currentPrize);
    }
    updateCell(cell, currentPrize, activePrize) {
        if (currentPrize) {
            cell.current();
        }
        else if (activePrize) {
            cell.active();
        }
        else {
            cell.inactive();
        }
    }
    isPrimary() {
        return (this.uiProperties.landscapePrimary && this.renderer.isLandscape()) || (this.uiProperties.portraitPrimary && this.renderer.isPortrait());
    }
    handleRotate(event) {
        super.handleRotate(event);
        this.uiProperties = this.uiProperties;
        const data = event?.getCurrentTarget() || {};
        this.visible = this.renderer.isLandscape(data.forceOrientation);
        if (this.backToGameButton) {
            this.backToGameButton.visible = this.renderer.isPortrait(data.forceOrientation);
        }
        for (let i = 0; i < this.cells.length; i++) {
            this.cells[i].updateSize(this.uiProperties.cellProperties[i]);
        }
    }
}
//# sourceMappingURL=AbstractPayTable.js.map